import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { Modal } from 'reactstrap';
import PropTypes from 'prop-types';
import moment from 'moment';
import GroupEditModalForm from './GroupEditModalForm';
import config from '../../../../config';
import Notify from '../../../../shared/components/widgets/Notify';

const GroupEditModal = ({ currentEditRow, changeShowEditModal, showEditModal, setForceRefresh, budgetAccountData, activeDayOptions, disableButton, setDisableButton, setShowEditModal, retailerConfig }) => {
  const sellerSelect = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const theme = useSelector(store => store.theme);

  const monthOptions = [
    { label: 'January', name: 'january', value: '1' },
    { label: 'February', name: 'february', value: '2' },
    { label: 'March', name: 'march', value: '3' },
    { label: 'April', name: 'april', value: '4' },
    { label: 'May', name: 'may', value: '5' },
    { label: 'June', name: 'june', value: '6' },
    { label: 'July', name: 'july', value: '7' },
    { label: 'August', name: 'august', value: '8' },
    { label: 'September', name: 'september', value: '9' },
    { label: 'October', name: 'october', value: '10' },
    { label: 'November', name: 'november', value: '11' },
    { label: 'December', name: 'december', value: '12' },
  ];

  const handleSubmit = (formData) => {
    setDisableButton(true);
    const activeDaysArray = [];
    const budgetObj = {
      monthly: {},
    };

    activeDayOptions.forEach(day => {
      activeDaysArray.push(formData[day.name] ? '1' : '0');
    });

    budgetObj.daily = {
      budget: parseFloat(formData.baseDailyBudgetAmount),
      reallocation: parseFloat(formData.reallocation),
    }

    monthOptions.forEach(month => {
      budgetObj.monthly[month.value] = {
        name: month.label,
        budget: parseFloat(formData[month.name]) || 0,
      };
    });

    // Check if we need to split items into individual budget groups
    if (formData.splitItemsCheckbox && formData.masterItemList) {
      // Parse the masterItemList to get individual items
      const itemList = formData.masterItemList
        .toString()
        .trim()
        .replace(/\r/g, '')
        .replace(/\s*,\s*/g, ',')
        .replace(/\n+/g, ',')
        .split(',')
        .filter(item => item.trim() !== '');

      if (itemList.length === 0) {
        setDisableButton(false);
        Notify.show('danger', 'Error', 'No items found in the master item list');
        return;
      }

      // Create a budget group for each item
      const postUrl = `${config.serverUrl}/accounts/budget-group-daily`;
      const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };

      let successCount = 0;
      let errorCount = 0;
      const promises = itemList.map((item) => {
        const itemBudgetGroupName = `${formData.budgetGroupName}_${item.trim()}`;

        const submitBudgetGroupDaily = {
          accountId: sellerSelect.value,
          accountTypeId: sellerSelect.type,
          budgetGroupDailyId: 0, // Always create new budget groups when splitting
          budgetGroupName: itemBudgetGroupName,
          setupFlow: formData.setupFlow?.value,
          retailer: retailerConfig.key,
          agencyInternal: formData.agencyInternal?.value,
          activeDaysCsv: activeDaysArray.join(','),
          allowedAdTypes: formData.allowedAdTypes.value,
          portfolioIdsCsv: formData.portfolioIdsCsv,
          includes: formData.includes.value,
          costTargetType: formData.costTargetType.value,
          costTargetMetric: formData.costTargetMetric,
          costTargetBrand: formData.costTargetType.value === 'bnb' ? formData.costTargetBrand : formData.costTarget,
          costTargetNonBrand: formData.costTargetType.value === 'bnb' ? formData.costTargetNonBrand : formData.costTarget,
          spendSpeed: formData.spendSpeed.value,
          spendTactic: formData.spendTactic.value,
          budgetType: formData.budgetType.value,
          spendPriority: formData.spendPriority.value,
          masterItemListOrig: item.trim(),
          masterItemList: item.trim(),
          startDate: formData.startDate,
          endDate: formData.endDate,
          budgetDriver: formData.budgetDriver.value,
          budgetJson: budgetObj,
          state: formData.state.value,
        };

        const requestOptions = {
          method: 'POST',
          headers: headerWithAuth,
          body: JSON.stringify(submitBudgetGroupDaily),
        };

        return fetch(postUrl, requestOptions)
          .then((results) => {
            if (!results.ok) {
              throw Error(results.statusText);
            }
            successCount += 1;
            return results.json();
          })
          .catch((error) => {
            errorCount += 1;
            console.error(`Error creating budget group for ${item}:`, error);
            return null;
          });
      });

      Promise.all(promises)
        .then(() => {
          changeShowEditModal();
          setDisableButton(false);
          setForceRefresh(moment().utc());

          if (successCount > 0) {
            Notify.show('success', 'Success', `Successfully created ${successCount} budget group(s)`);
          }
          if (errorCount > 0) {
            Notify.show('warning', 'Warning', `Failed to create ${errorCount} budget group(s)`);
          }
        })
        .catch((error) => {
          Notify.show('danger', 'Error', `An error occurred while creating budget groups: ${error}`);
          console.error(error);
          setDisableButton(false);
        });
    } else {
      // Original logic for single budget group
      const submitBudgetGroupDaily = {
        accountId: sellerSelect.value,
        accountTypeId: sellerSelect.type,
        budgetGroupDailyId: formData.budgetGroupDailyId || 0,
        budgetGroupName: formData.budgetGroupName,
        setupFlow: formData.setupFlow?.value,
        retailer: retailerConfig.key,
        agencyInternal: formData.agencyInternal?.value,
        activeDaysCsv: activeDaysArray.join(','),
        allowedAdTypes: formData.allowedAdTypes.value,
        portfolioIdsCsv: formData.portfolioIdsCsv,
        includes: formData.includes.value,
        costTargetType: formData.costTargetType.value,
        costTargetMetric: formData.costTargetMetric,
        costTargetBrand: formData.costTargetType.value === 'bnb' ? formData.costTargetBrand : formData.costTarget,
        costTargetNonBrand: formData.costTargetType.value === 'bnb' ? formData.costTargetNonBrand : formData.costTarget,
        spendSpeed: formData.spendSpeed.value,
        spendTactic: formData.spendTactic.value,
        budgetType: formData.budgetType.value,
        spendPriority: formData.spendPriority.value,
        masterItemListOrig: formData.masterItemListOrig,
        masterItemList: formData.masterItemList,
        startDate: formData.startDate,
        endDate: formData.endDate,
        budgetDriver: formData.budgetDriver.value,
        budgetJson: budgetObj,
        state: formData.state.value,
      };

      if (formData.budgetGroupName) {
        const postUrl = `${config.serverUrl}/accounts/budget-group-daily`;
        const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };
        const requestOptions = {
          method: 'POST',
          headers: headerWithAuth,
          body: JSON.stringify(submitBudgetGroupDaily),
        };

        fetch(postUrl, requestOptions)
          .then((results) => {
            if (!results.ok) {
              throw Error(results.statusText);
            }
            return results.json();
          }).then(() => {
            changeShowEditModal();
            setDisableButton(false);
            setForceRefresh(moment().utc());
          }).catch((error) => {
            Notify.show('danger', 'Error', `We were unable to update your budget. Error: ${error}`);
            console.error(error);
            setDisableButton(false);
          });
      }
    }
  };

  const modalClasses = classNames({
    'todo__add-modal': true,
  }, theme.className, 'ltr-support');

  return (
    <Modal
      isOpen={showEditModal}
      toggle={changeShowEditModal}
      className={modalClasses}
    >
      <GroupEditModalForm
        onSubmit={handleSubmit}
        initialValues={currentEditRow}
        // handleSubmit={handleSubmit} // Try to figure this out.  onSubmit and initalValues seem to be defualt props for redux?
        currentEditRow={currentEditRow}
        changeShowEditModal={changeShowEditModal}
        activeDayOptions={activeDayOptions}
        monthOptions={monthOptions}
        user={user}
        budgetAccountData={budgetAccountData}
        disableButton={disableButton}
        setDisableButton={setDisableButton}
        setShowEditModal={setShowEditModal}
        retailerConfig={retailerConfig}
      />
    </Modal>
  );
};

GroupEditModal.propTypes = {
  currentEditRow: PropTypes.shape(),
  changeShowEditModal: PropTypes.func.isRequired,
  showEditModal: PropTypes.bool.isRequired,
  setForceRefresh: PropTypes.func.isRequired,
};

GroupEditModal.defaultProps = {
  currentEditRow: null,
};

export default GroupEditModal;
